import axios from 'axios';
import { APIV2 } from '../../../backend';
import { useAppContext } from '../../../hooks/context/AppContext';
import { useState, useMemo } from 'react';
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

// Interfaces
export interface PlanningStrategyPayload {
  Project_Key: number;
  Volumey1: number;
  Volumey1_Measure: string;
  Volumey3: number;
  Volumey3_Measure: string;
  cannibalisation: number;
  Supplier_Utilisation_Strategy_key: number;
  InHouse_CM_Key: number;
  Sourcing_Unit_Region: number;
  Sourcing_Unit_subRegion: number;
  Sourcing_Unit_country: number;
  Comment: string;
  CM_region: string;
  Cm: string;
  SLA_Complete_Key: number;
  Avaialble_Capacity: number;
  Cpt_Compliance_Key: string;
  Risk_Key: number;
  Created_By: string;
  Updated_By: string;
}

export interface PlanningStrategyPayloadResponse {
  Project_Key: number;
  Volumey1: number;
  Volumey1_Measure: string;
  Volumey3: number;
  Volumey3_Measure: string;
  cannibalisation: number;
  InHouse_CM_Key: number;
  Supplier_Utilisation_Strategy_key: number | null;
  Sourcing_Unit_Region: number;
  Sourcing_Unit_country: number;
  CM_region: number | null;
  Cm: number | null;
  SLA_Complete_Key: number;
  Cpt_Compliance_Key: number;
  Comment: string;
  Risk_Key: number;
  Created_By: string;
  Updated_By: string | null;
  Risk_Name: string;
  Project_Name: string;
}

// Custom hook for Planning Strategy API client
export const usePlanningStrategyApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh((token: any | null, account: any | null) => {
    setIdToken(token);
  });

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken ? `Bearer ${idToken}` : `Bearer ${authData.idToken}`,
      },
    });

     // Add response interceptor to handle 401 errors
     instance.interceptors.response.use(
      response => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const {token}  = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error('Error refreshing token:', refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );

    
    return instance;
  }, [idToken, authData.idToken]);

  // Lookup APIs
  const getInHouse = async () => {
    try {
      const response = await apiClient.get('/lookup/PS_inhouse');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching in-house options');
    }
  };

  const getSupplierUtilisation = async () => {
    try {
      const response = await apiClient.get('/lookup/PS_Supplier_Utilisation');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching supplier utilisation options');
    }
  };

  const getCptCompliance = async () => {
    try {
      const response = await apiClient.get('/statusFlag');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching CPT compliance options');
    }
  };

  const getPlanningStrategyRisk = async () => {
    try {
      const response = await apiClient.get('/risks');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching planning strategy risks');
    }
  };

  const getCMRegion = async () => {
    try {
      const response = await apiClient.get('/cmRegion');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching CM regions');
    }
  };

  const getcm = async (regionKey?: number) => {
    try {
      const url = regionKey ? `/cm?regionKey=${regionKey}` : '/cm';
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching CMs');
    }
  };

  const getSourcingUnitRegion = async () => {
    try {
      const response = await apiClient.get('/sourceUnitRegion');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching sourcing unit regions');
    }
  };

  const getSourceUnitCountry = async (SourceUnitRegionKey?: number) => {
    try {
      const url = SourceUnitRegionKey 
        ? `/sourceUnitCountry?SourceUnitRegionKey=${SourceUnitRegionKey}`
        : '/sourceUnitCountry';
      const response = await apiClient.get(url);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching sourcing unit countries');
    }
  };

  // Planning Strategy CRUD APIs
  const getPlanningStrategy = async (project_key: any) => {
    try {
      const response = await apiClient.get(`/planningStrategy/${project_key}`);
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching planning strategy');
    }
  };

  const createPlanningStrategy = async (payload: PlanningStrategyPayload) => {
    try {
      const response = await apiClient.post('/planningStrategy', payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'creating planning strategy');
    }
  };

  const updatePlanningStrategy = async (payload: PlanningStrategyPayload, project_key: any) => {
    try {
      const response = await apiClient.put(`/planningStrategy/${project_key}`, payload);
      return response.data;
    } catch (err) {
      return handleError(err, 'updating planning strategy');
    }
  };

  // Static options
  const getUnits = () => [
    { key: "Tonne", value: "Tonne" },
    { key: "Units", value: "000 Units" },
  ];
  
  const getCannibalisation = async () => {
    try {
      const response = await apiClient.get('/lookup/PS_Cannibalisation');
      return response.data;
    } catch (err) {
      return handleError(err, 'fetching Cannibalisation options');
    }
  }; 
  const getSlaComplete = async () => {
    try {
      const response = await apiClient.get('/lookup/PS_SLA');
      return response.data;
    } catch (err) {
      return handleError(err, "fetching SLA options");
    }
  }; 
  return {
    // Lookup methods
    getInHouse,
    getSupplierUtilisation,
    getCptCompliance,
    getPlanningStrategyRisk,
    getCMRegion,
    getcm,
    getSourcingUnitRegion,
    getSourceUnitCountry,
    
    // Planning Strategy CRUD methods
    getPlanningStrategy,
    createPlanningStrategy,
    updatePlanningStrategy,
    
    // Static options
    getUnits,
    getCannibalisation,
    getSlaComplete,
  };
};